<template>
  <s-dialog
    :visible.sync="visible"
    :handle-cancel="handleCancel"
    :handle-confirm="handleConfirm"
    :show-default-footer="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :show-close="false"
    :title="$i18n('About to Log Out')"
    :cancel-button-text="$i18n('Continue to use')"
    :confirm-button-text="$i18n('Logout directly')"
    size="medium"
    @open="handleOpen"
  >
    <p>
      {{
        $i18n(
          'You have not operated the system for a long time, the system has %s seconds remaining to Logout automatically.',
          [countdown]
        )
      }}
    </p>
  </s-dialog>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { logout } from '@/utils/tobLogin';
import { throttle } from 'lodash';

enum IdleState {
  ACTIVE = 'active',
  WARNING = 'warning',
  LOGOUT = 'logout',
}

// 交互事件类型
const INTERACTION_EVENTS = ['mousemove', 'click', 'keydown'];

const DEFAULT_CONFIG = {
  WARNING_TIME: 60 * 1000, // 1分钟提醒时间
  IDLE_CHECK_INTERVAL: 1000, // 1秒钟检查一次
  IDLE_CHECK_TIME: 120, // 默认120分钟
  IDLE_CHECK_MIN_TIME: 2, // 最小值2分钟
};

const STORAGE_KEYS = {
  LAST_ACTIVITY_TIME: 'wms_last_activity_time',
  IDLE_STATE: 'wms_idle_state',
};

@Component({})
export default class IdleDetector extends Vue {
  visible = false;
  countdown = 0;

  private state: IdleState = IdleState.ACTIVE;
  private lastActivityTime = Date.now();
  private warningTime = DEFAULT_CONFIG.WARNING_TIME;
  private warningStartTime = 0;

  private idleTimer: number | null = null;
  private checkTimer: number | null = null;
  private currentScrollElement: EventTarget | null = null;
  private mutationObserver: MutationObserver | null = null;
  private scrollBindingTimeout: number | null = null;

  private throttledResetIdleTimer = throttle(this.executeResetIdleTimer, 1000, {
    leading: true,
    trailing: false,
  });

  get idleThreshold(): number {
    // 默认0不起作用，最小值2min，提前1min弹窗提示
    let threshold = this.$store.state.system.autoLogoutThreshold;
    if (isNaN(threshold)) {
      threshold = 0;
    }
    threshold =
      threshold > 0 && threshold < DEFAULT_CONFIG.IDLE_CHECK_MIN_TIME
        ? DEFAULT_CONFIG.IDLE_CHECK_MIN_TIME
        : threshold;
    return (threshold - 1) * 60 * 1000;
  }

  @Watch('idleThreshold')
  onIdleThresholdChange(newVal: number) {
    if (newVal > 0) {
      this.startDetection();
    } else {
      this.onDestroy();
    }
  }

  beforeDestroy() {
    if (this.idleThreshold > 0) {
      this.onDestroy();
    }
  }

  private startDetection() {
    this.lastActivityTime = Date.now();
    this.state = IdleState.ACTIVE;

    // 绑定 storage 事件监听其他 tab 的状态变化
    this.bindStorageEvent();

    // 绑定交互事件
    this.bindInteractionEvents();

    // 绑定标签页可见性事件
    this.bindVisibilityEvents();

    // 启动空闲检测
    this.resetIdleTimer();
  }

  private stopDetection() {
    this.clearAllTimers();
    this.unbindStorageEvent();
    this.unbindInteractionEvents();
    this.unbindVisibilityEvents();
    this.stopMutationObserver();
  }

  private onDestroy() {
    this.stopDetection();
    this.throttledResetIdleTimer.cancel();
  }

  private bindStorageEvent() {
    window.addEventListener('storage', this.onStorageChange);
  }

  private unbindStorageEvent() {
    window.removeEventListener('storage', this.onStorageChange);
  }

  private onStorageChange(event: StorageEvent) {
    if (event.key === STORAGE_KEYS.LAST_ACTIVITY_TIME) {
      const newLastActivityTime = parseInt(event.newValue || '0', 10);
      if (newLastActivityTime > this.lastActivityTime) {
        this.lastActivityTime = newLastActivityTime;
        if (this.state === IdleState.WARNING) {
          this.cancelWarning();
        }
      }
    } else if (event.key === STORAGE_KEYS.IDLE_STATE) {
      const newState = event.newValue;
      if (newState === IdleState.LOGOUT && this.state !== IdleState.LOGOUT) {
        this.visible = false;
        this.state = IdleState.LOGOUT;
        this.stopCountdown();
        logout();
      }
    }
  }

  private updateStorageLastActivityTime() {
    localStorage.setItem(
      STORAGE_KEYS.LAST_ACTIVITY_TIME,
      this.lastActivityTime.toString()
    );
  }

  private updateStorageIdleState() {
    localStorage.setItem(STORAGE_KEYS.IDLE_STATE, this.state);
  }

  private bindInteractionEvents() {
    INTERACTION_EVENTS.forEach((event) => {
      document.addEventListener(event, this.resetIdleTimer);
    });

    this.$router.afterEach(() => {
      setTimeout(this.delayedBindScrollEvent, 3000);
    });
  }

  private unbindInteractionEvents() {
    INTERACTION_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.resetIdleTimer);
    });
    this.unbindScrollEvent();
  }

  private delayedBindScrollEvent() {
    this.unbindScrollEvent();

    if (this.scrollBindingTimeout) {
      clearTimeout(this.scrollBindingTimeout);
    }

    this.bindScrollEvent();

    // 启动 MutationObserver 监听 DOM 变化
    this.startMutationObserver();

    // 兜底方案：如果 MutationObserver 没有触发，3秒后强制绑定
    this.scrollBindingTimeout = window.setTimeout(() => {
      this.bindScrollEvent();
      this.stopMutationObserver();
    }, 3000);
  }

  private startMutationObserver() {
    this.stopMutationObserver();

    this.mutationObserver = new MutationObserver((mutations) => {
      // 检查是否有 .main-page 元素被添加
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.classList.contains('main-page') ||
                element.querySelector('.main-page')
              ) {
                setTimeout(() => {
                  this.bindScrollEvent();
                  this.stopMutationObserver();
                }, 50);
                return;
              }
            }
          }
        }
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private stopMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this.scrollBindingTimeout) {
      clearTimeout(this.scrollBindingTimeout);
      this.scrollBindingTimeout = null;
    }
  }

  private bindScrollEvent() {
    this.unbindScrollEvent();

    const scrollContainer = document.querySelector('.main-page');

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', this.resetIdleTimer, {
        passive: true,
      });
      this.currentScrollElement = scrollContainer;
    }
  }

  private unbindScrollEvent() {
    if (this.currentScrollElement) {
      this.currentScrollElement.removeEventListener(
        'scroll',
        this.resetIdleTimer
      );
      this.currentScrollElement = null;
    }
  }

  private bindVisibilityEvents() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private unbindVisibilityEvents() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private resetIdleTimer() {
    if (this.state === IdleState.WARNING) {
      return;
    }
    this.throttledResetIdleTimer();
  }

  private executeResetIdleTimer() {
    this.lastActivityTime = Date.now();
    this.state = IdleState.ACTIVE;
    this.updateStorageLastActivityTime();

    this.startIdleCheck();
  }

  private startIdleCheck() {
    const checkTimeout = Math.min(
      this.idleThreshold,
      DEFAULT_CONFIG.IDLE_CHECK_INTERVAL
    );

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.idleTimer = window.setTimeout(this.checkIdleStatus, checkTimeout);
  }

  private checkIdleStatus() {
    const now = Date.now();
    const idleDuration = now - this.lastActivityTime;

    if (idleDuration >= this.idleThreshold) {
      this.onIdle();
    } else {
      // 继续检查，使用剩余时间作为间隔
      const remainingTime = this.idleThreshold - idleDuration;
      const nextCheckTimeout = Math.min(
        remainingTime,
        DEFAULT_CONFIG.IDLE_CHECK_INTERVAL
      );

      this.idleTimer = window.setTimeout(
        this.checkIdleStatus,
        nextCheckTimeout
      );
    }
  }

  private onIdle() {
    this.showWarning();
  }

  private showWarning() {
    this.state = IdleState.WARNING;
    this.visible = true;
  }

  private cancelWarning() {
    this.state = IdleState.ACTIVE;
    this.visible = false;
    this.stopCountdown();
    this.resetIdleTimer();
  }

  private startCountdown() {
    const tick = () => {
      const elapsed = Date.now() - this.warningStartTime;
      const remaining = this.warningTime - elapsed;

      if (remaining <= 0) {
        this.executeLogout();
        return;
      }

      this.countdown = this.millisecondsToSeconds(remaining);
      this.checkTimer = window.setTimeout(tick, 1000);
    };

    tick();
  }

  private stopCountdown() {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
  }

  private onVisibilityChange() {
    if (!document.hidden) {
      this.handleTabResume();
    }
  }

  private handleTabResume() {
    if (this.state === IdleState.WARNING) {
      this.recalculateRemainingTime();
    } else {
      const totalIdleTime = Date.now() - this.lastActivityTime;
      if (totalIdleTime >= this.idleThreshold) {
        this.showWarning();
      }
    }
  }

  private recalculateRemainingTime() {
    const now = Date.now();
    const actualElapsed = now - this.warningStartTime;
    const remaining = this.warningTime - actualElapsed;

    if (remaining <= 0) {
      this.executeLogout();
    } else {
      this.countdown = this.millisecondsToSeconds(remaining);
    }
  }

  private async executeLogout() {
    this.visible = false;
    this.state = IdleState.LOGOUT;
    this.updateStorageIdleState();
    this.stopCountdown();
    await logout();
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY_TIME);
    localStorage.removeItem(STORAGE_KEYS.IDLE_STATE);
  }

  private clearAllTimers() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    if (this.scrollBindingTimeout) {
      clearTimeout(this.scrollBindingTimeout);
      this.scrollBindingTimeout = null;
    }
  }

  handleOpen() {
    this.warningStartTime = Date.now();
    this.countdown = this.millisecondsToSeconds(this.warningTime);
    this.startCountdown();
  }

  handleCancel() {
    this.cancelWarning();
  }

  handleConfirm() {
    this.executeLogout();
  }

  private millisecondsToSeconds(milliseconds: number): number {
    return Math.ceil(milliseconds / 1000);
  }
}
</script>

<style scoped lang="scss">
.countdown {
  color: #f32345;
}
</style>
